import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';
import {
  TNormalizedPeople,
  TNormalizedPeopleCompact,
  TPeoplePayload,
} from 'types';
import {
  getResponseData,
  isNumeric,
  notifyError,
  notifySuccess,
  setValidationErrors,
} from 'utils';
import request from 'axios';

type State = {
  members: TNormalizedPeople[];
  pagination: {
    sortBy: string;
    page: number;
    perPage: number;
    lastPage: number;
    total: number;
    totalPages: number;
    surname: string;
    forenames: string;
    unitId: number | null;
    unitName: string;
  };
  loading: boolean;
};

export const useMembersStore = defineStore('members', {
  state: (): State => ({
    members: [],
    pagination: {
      sortBy: 'surname',
      page: 1,
      perPage: 12,
      lastPage: 1,
      total: 0,
      totalPages: 0,
      surname: '',
      forenames: '',
      unitId: null,
      unitName: '',
    },
    loading: true,
  }),
  actions: {
    async getMembers() {
      this.loading = true;

      const sort = `&sort=${this.pagination.sortBy}`;
      const pg = `&pg=${this?.pagination?.page ?? 1}`;
      const ps = `&ps=${this?.pagination?.perPage ?? 9}`;

      const surname = this.pagination.surname
        ? `&filter[surname]=${this.pagination.surname}`
        : '';

      const forenames = this.pagination.forenames
        ? `&filter[forenames]=${this.pagination.forenames}`
        : '';

      const unitId = isNumeric(this?.pagination?.unitId)
        ? `&filter[unit_id]=${this.pagination.unitId}`
        : '';

      const response = await api.get(
        `/api/v1/people?${pg}${ps}${sort}${surname}${forenames}${unitId}`
      );

      this.members = getResponseData(response) as TNormalizedPeople[];

      const paginationData = response.data.meta.page;
      this.pagination = {
        ...this.pagination,
        page: paginationData.current_page,
        perPage: paginationData.per_page,
        lastPage: paginationData.total_pages,
        total: paginationData.total,
        totalPages: paginationData.total_pages,
      };
      this.loading = false;
    },
    async getMember(id: number, skipCache = false) {
      const response = await api.get(
        `/api/v1/people/${id}${skipCache ? '?oc=1' : ''}`
      );
      return getResponseData(response) as TNormalizedPeopleCompact;
    },
    async updateMemberDetails(id: number, payload: TPeoplePayload) {
      try {
        const response = await api.put(`/api/v1/people/${id}`, payload);
        if (response.status === 200) {
          notifySuccess('Member details were updated successfully.');
          return true;
        }
        notifyError(
          'There was a problem updating member details, a reload of the application may fix this.'
        );
        return false;
      } catch (err) {
        notifyError();
        if (request.isAxiosError(err) && err.response) {
          setValidationErrors(err.response.data.errors);
        }
        return false;
      }
    },
    clear() {
      this.members = [];
      this.pagination = {
        sortBy: 'surname',
        page: 1,
        perPage: 12,
        lastPage: 1,
        total: 0,
        totalPages: 0,
        surname: '',
        forenames: '',
        unitId: null,
        unitName: '',
      };
      this.loading = false;
    },
  },
});
